import React from "react";
import { graphql, Link } from "gatsby";
import Layout from "../components/layout";
import SEO from "../components/seo";
import styled from "styled-components";
import {
  mainBlue,
  lightGray,
  mainWhite,
  mainBlack,
  graphik,
  larsseit,
  screen,
} from "../components/common/variables";
import AnimatedHeading from "../components/animated-heading/home-page";
import LabeledArrow from "../components/labeled-arrow";
import CarouselTestimonials from "../components/carousel-testimonials";
import ClientSectionSmall from "../components/client-logo-section/small-screen";
import ClientSectionLarge from "../components/client-logo-section/large-screen";
import ContactForm from "../components/contact-form";

const Wrapper = styled.div`
  .home-intro {
    padding: 0 27px 173px 27px;
    background: ${lightGray};
    @media ${screen.xsmall} {
      padding: 0 60px 150px 60px;
    }
    @media ${screen.small} {
      padding: 0 80px 150px 80px;
    }
    @media ${screen.medium} {
      padding: 0 7vw 150px 7vw;
    }

    &__title {
      h1 {
        color: ${mainBlack};
        font-family: ${larsseit};
        line-height: 1.23;
        font-size: 2.53rem;
        font-weight: 400;
        @media ${screen.xsmall} {
          font-size: 3.3rem;
        }
        @media ${screen.small} {
          font-size: 4rem;
        }
        @media ${screen.medium} {
          font-size: clamp(1.4rem, calc(1.4rem + 3vw), 5.01rem);
        }

        strong {
          color: ${mainBlue};
          font-weight: 400;
        }

        .break {
          @media ${screen.xsmall} {
            display: block;
          }
        }
      }
    }

    .navigate {
      margin: 20px 0 0 0;
      @media ${screen.xsmall} {
        margin: 49px 0 0 0;
      }
    }
  }

  .home-body {
    background: ${mainWhite};
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 0 27px 63px 27px;
    @media ${screen.xsmall} {
      padding: 0 60px 150px 60px;
    }
    @media ${screen.small} {
      padding: 0 80px 150px 80px;
    }
    @media ${screen.medium} {
      padding: 0 7vw 150px 7vw;
    }

    &__gif {
      margin: -130px 0 0 0;
      cursor: pointer;

      img {
        display: block;
        width: 373px;
        margin: 0 -3.6vw 0 0;

        @media ${screen.xsmall} {
          width: 100%;
          max-width: 800px;
          margin: 0;
        }
        @media ${screen.large} {
          max-width: 920px;
        }
        @media ${screen.xlarge} {
          max-width: 990px;
        }
      }
    }

    &__description {
      color: ${mainBlack};
      font-family: ${graphik};
      max-width: 700px;
      margin: 45px 0 0 0;
      @media ${screen.medium} {
        max-width: 750px;
      }
      @media ${screen.large} {
        max-width: 800px;
      }
      @media ${screen.xlarge} {
        max-width: 850px;
      }

      p,
      h2 {
        font-size: 1.1rem;
        line-height: 1.6;
        @media ${screen.small} {
          font-size: 1.15rem;
        }
        @media ${screen.medium} {
          font-size: 1.33rem;
        }
      }

      ul {
        padding: 5px 0 0 20px;
        @media ${screen.xsmall} {
          padding: 5px 0 0 30px;
        }
      }

      li {
        font-size: 1.1rem;
        margin: 0 0 5px 0;
        position: relative;
        list-style-type: disc;
        @media ${screen.small} {
          font-size: 1.15rem;
        }
        @media ${screen.medium} {
          font-size: 1.33rem;
        }
      }
    }

    .navigate {
      max-width: 700px;
      width: 100%;
      margin: 25px 0 0 0;
      @media ${screen.xsmall} {
        margin: 40px 0 0 0;
      }
      @media ${screen.medium} {
        max-width: 750px;
      }
      @media ${screen.large} {
        max-width: 800px;
      }
      @media ${screen.xlarge} {
        max-width: 850px;
      }
    }
  }
`;

const IndexPage = ({ data }) => {
  // all content from prismic via graphql query at the bottom
  const {
    title_tag,
    meta_description,
    intro_description,
    testimonial_list,
    featured_link_url,
    gif,
    client_heading,
    client_description,
    client_list,
  } = data.content.data;

  // return a string of subpage url
  const getUrlPathname = (url) => {
    let path;
    if (url) {
      if (url.indexOf("http") === -1) {
        return "/";
      } else {
        path = new URL(url);
        return path.pathname;
      }
    } else {
      return "/";
    }
  };

  // use this link path for featured work to be used for <Link/> gatsby component
  // prevents to use generic <a/> tag as it loads the page when navigating.
  const featuredSubLinkURL = getUrlPathname(featured_link_url);

  return (
    <Layout colourNavbar={lightGray} isHomePage>
      <SEO title={title_tag} description={meta_description} isHomePage />
      <Wrapper>
        <div className="home-intro">
          <div className="home-intro__title">
            <AnimatedHeading />
          </div>

          <div className="navigate">
            <LabeledArrow label="SEE HOW OUR CLIENTS DO IT" url="/our-work/" />
          </div>
        </div>

        <div className="home-body">
          <Link
            id="main-featured-work"
            className="home-body__gif"
            to={featuredSubLinkURL}
            data-sal="slide-up"
            data-sal-duration="500"
          >
            <img src={gif.url} alt={gif.alt || "Featured"} />
          </Link>
          <div
            className="home-body__description"
            data-sal="slide-up"
            data-sal-duration="500"
            dangerouslySetInnerHTML={{ __html: intro_description.html }}
          />
          <div className="navigate" data-sal="slide-up" data-sal-duration="500">
            <LabeledArrow label="LEARN ABOUT OUR SERVICES" url="/what-we-do/" />
          </div>
        </div>

        <CarouselTestimonials testimonial_list={testimonial_list} />

        {/* static clients logo but shuffled */}
        <ClientSectionSmall
          client_heading={client_heading}
          client_description={client_description}
          client_list={client_list}
        />

        {/* clients logo with carousel */}
        <ClientSectionLarge
          client_heading={client_heading}
          client_description={client_description}
          client_list={client_list}
        />

        {/*  form section right before footer with blue background */}
        <ContactForm />
      </Wrapper>
    </Layout>
  );
};

export default IndexPage;

export const dataQuery = graphql`
  {
    content: prismicHomePage {
      data {
        title_tag
        meta_description
        intro_description {
          html
        }
        testimonial_list {
          full_name
          company
          heading {
            html
          }
          message {
            html
          }
        }
        featured_link_url
        gif {
          alt
          url
        }
        client_heading
        client_description
        client_list {
          website_url
          logo {
            alt
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
`;
